/**
 * Typed corpus loaders and the font PORT — §3.1, L1, L5.
 *
 * L1 IS STILL ENFORCED STRUCTURALLY HERE, and now by the type system rather than by
 * this file owning the browser API: `loadPageWithFonts` takes a `FontLoader` as a
 * REQUIRED argument and awaits it before returning, so there is no code path that
 * hands a component page data without the font that renders it. QCF4 encodes each WORD
 * as one glyph in the Unicode Private Use Area; those codepoints are meaningless in
 * any other typeface, so a fallback face renders a screen of tofu.
 *
 * WHY THIS IS A PORT AT ALL. `FontFace` and `document.fonts` are browser APIs, and
 * `@mushaf/corpus` is imported by a React Native app (`apps/mobile`) where they do not
 * exist. The platform halves live in `apps/web/src/lib/fonts.ts` and
 * `apps/mobile/src/lib/fonts.ts`. Nothing platform-specific may come back into this
 * file.
 */
import { CORPUS_BASE } from './constants'
import type { Meta, MushafPage, ReciterAudio, SearchRow, TafsirPage } from './types'

const pad = (page: number) => String(page).padStart(3, '0')

/**
 * The URL builders, as a factory over the base.
 *
 * The base is a parameter because it is not the same shape on both platforms: the web
 * app serves the corpus from the site root (`/corpus`), while the native app reads it
 * out of its own document directory as a `file://` URI. Everything below the base is
 * identical, because it is a property of the corpus rather than of the host.
 */
export function makeCorpusUrls(base: string) {
  return {
    meta: () => `${base}/meta.json`,
    search: () => `${base}/search.json`,
    page: (page: number) => `${base}/mushaf/${pad(page)}.json`,
    tafsir: (page: number) => `${base}/tafsir/${pad(page)}.json`,
    audio: (reciterId: string) => `${base}/audio/${reciterId}.json`,
    /**
     * The family name in the page data is NOT the filename.
     *
     * The 47 page faces are named `QCF4_Hafs_NN` in the JSON but ship as
     * `QCF4_Hafs_NN_W.woff2`; the bismillah face `QCF4_QBSML` ships under its own name
     * with no suffix. Getting this wrong 404s every page font silently, the browser
     * falls back to a face that has no PUA glyphs, and the whole page is tofu (L1) —
     * while the layout measurement happily converges on the WRONG advance widths.
     */
    pageFont: (font: string) =>
      `${base}/fonts/${font.startsWith('QCF4_Hafs_') ? `${font}_W` : font}.woff2`,
    v4Font: (page: number, ext: 'woff2' | 'ttf') => `${base}/fonts/v4tajweed/p${page}.${ext}`,
    /**
     * Janna LT ships TWO weights only — Regular and Bold. The UI declares 600 as well,
     * so 600 and 700 resolve to the SAME Bold file rather than shipping a duplicate or
     * letting the browser synthesise a semibold (synthetic bold smears Arabic joins).
     * Keep this in step with the three `@font-face` blocks in `app.css`.
     */
    uiFont: (weight: 400 | 600 | 700) =>
      `${base}/fonts/ui/janna-lt-${weight === 400 ? 400 : 700}.woff2`,
  } as const
}

export type CorpusUrls = ReturnType<typeof makeCorpusUrls>

/** The web default. `apps/mobile` builds its own against the document directory. */
export const corpusUrls = makeCorpusUrls(CORPUS_BASE)

// ---------------------------------------------------------------------------
// JSON transport
// ---------------------------------------------------------------------------

export type JsonReader = <T>(url: string, signal?: AbortSignal) => Promise<T>

/**
 * The web default. React Native's `fetch` does not read `file://` reliably, so the
 * native app injects a reader built on `expo-file-system` instead.
 */
export const fetchJson: JsonReader = async <T>(url: string, signal?: AbortSignal): Promise<T> => {
  const response = await fetch(url, { signal })
  if (!response.ok) throw new Error(`${url} → ${response.status}`)
  return (await response.json()) as T
}

export const loadMeta = (signal?: AbortSignal) => fetchJson<Meta>(corpusUrls.meta(), signal)
export const loadSearchIndex = (signal?: AbortSignal) =>
  fetchJson<SearchRow[]>(corpusUrls.search(), signal)
export const loadTafsirPage = (page: number, signal?: AbortSignal) =>
  fetchJson<TafsirPage>(corpusUrls.tafsir(page), signal)
export const loadReciterAudio = (id: string, signal?: AbortSignal) =>
  fetchJson<ReciterAudio>(corpusUrls.audio(id), signal)

// ---------------------------------------------------------------------------
// Fonts — the port
// ---------------------------------------------------------------------------

/**
 * L5 on the web: ALL 604 v4 TAJWEED COLOUR FONTS REGISTER UNDER ONE FAMILY NAME.
 * `@font-palette-values` is declared per family, and 604 families would need 604
 * duplicate palette declarations, so the previous face is removed before the next is
 * added.
 *
 * ON NATIVE THAT REASON DOES NOT EXIST AND THE LAW INVERTS. React Native has no
 * font-palette API whatsoever, so one family buys nothing — and `expo-font` explicitly
 * cannot swap a file under an existing family name. The native adapter therefore
 * registers `V4TJ_p{page}` per page. That is why the family name is the ADAPTER's
 * choice, exposed through `v4FamilyFor`, and not a constant this file dictates.
 */
export const V4_FAMILY = 'V4TJ'

export interface FontLoader {
  /**
   * Make every page face usable. Must not resolve until the faces are actually
   * available for layout — that promise is the whole of L1.
   */
  loadPageFaces(faces: readonly { family: string; url: string }[]): Promise<void>
  /**
   * Make the v4 colour face for `page` usable, trying `candidates` IN ORDER. Returns
   * whether one succeeded; must not throw (§7.7: loading the v4 font is
   * failure-tolerant — fall through to the monochrome glyphs rather than blanking the
   * page).
   */
  loadV4Face(page: number, candidates: readonly string[]): Promise<boolean>
  /** The family name this adapter registered `page`'s v4 face under. */
  v4FamilyFor(page: number): string
}

export interface LoadedPage {
  data: MushafPage
  /** Whether the v4 colour font for this page is available. */
  v4Ready: boolean
}

/**
 * Load the page JSON AND its fonts together. This is L1, enforced structurally rather
 * than by discipline: `fonts` is required and awaited, so there is no code path that
 * hands a component page data without the font it needs.
 *
 * `withV4` is passed rather than read from settings so this stays a pure-ish loader.
 */
export async function loadPageWithFonts(
  page: number,
  options: {
    meta: Meta
    withV4: boolean
    fonts: FontLoader
    /** Defaults to the web base; `apps/mobile` passes its document-directory build. */
    urls?: CorpusUrls
    /** Defaults to `fetch`; `apps/mobile` passes an `expo-file-system` reader. */
    readJson?: JsonReader
    signal?: AbortSignal
  },
): Promise<LoadedPage> {
  const { meta, withV4, fonts, urls = corpusUrls, readJson = fetchJson, signal } = options
  const data = await readJson<MushafPage>(urls.page(page), signal)

  const families = new Set<string>([data.font])
  for (const line of data.lines) {
    for (const word of line.words) families.add(word.font)
  }
  families.add(meta.bismillahFont)

  await fonts.loadPageFaces([...families].map((family) => ({ family, url: urls.pageFont(family) })))

  /**
   * PAGE 328 EXISTS ONLY AS `.ttf` UPSTREAM — the candidate order is a fact about the
   * corpus, not about the platform, so it stays here rather than in the adapters. Get
   * it wrong and one page of the mushaf is unrenderable (T6).
   */
  const v4Ready = withV4
    ? await fonts.loadV4Face(page, [urls.v4Font(page, 'woff2'), urls.v4Font(page, 'ttf')])
    : false

  return { data, v4Ready }
}
